import { Flex } from 'antd';

import ObjectTree from '@components/objectTree/ObjectTree';
import BaseText from '@components/typography/BaseText';
import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';
import { ANNOTATION_PHASE } from '@utils/constants';

import AnnotationObjectCard from './AnnotationObjectCard';
import ObjectLabels from './ObjectLabels';
import { useAnnotationActiveNode } from './useAnnotationActiveNode';

const eyebrowStyle = { textTransform: 'uppercase' };

const MESSAGES = {
	error: "Couldn't load the objects. Please try again.",
	empty: 'No objects for this project type yet.',
};

/**
 * The object picker of annotation mode: the project being annotated, and the
 * caller's taxonomy for its project type with the active object marked. A click
 * on an object with a model makes it the active one (kept in the project
 * context, so the draw tools can read it); a grouping object — no model for
 * this project type — only explains itself.
 *
 * Until the user picks one, the first object with a model is active (see
 * `useAnnotationActiveNode`). Once tiles exist, picking another object switches
 * the session to it: the annotator reloads the tiles for that object inside
 * the same boundary. Every box drawn so far stays on the map, saved or not.
 */
const AnnotationObjectTree = () => {
	const { project, projectMode, annotationMode, dispatch } = useProject();
	const { addToast } = useToast();
	const { node: activeNode, tree, isLoading, isError } = useAnnotationActiveNode();
	const activeUuid = activeNode?.uuid ?? null;

	const isPicking = annotationMode?.phase === ANNOTATION_PHASE.PICK;
	const isAnnotating = annotationMode?.phase === ANNOTATION_PHASE.ANNOTATE;

	const handleSelect = async (uuid, node) => {
		if (uuid === activeUuid) return;

		if (!node.is_available) {
			addToast({
				id: 'annotation-grouping-object',
				bg: 'warning',
				title: `${node.name} has no model for this project type. Pick an object under it.`,
			});
			return;
		}

		// The open tile may hold unsaved changes: the annotator asks first ("Save and
		// switch" or "Stay") and answers whether the switch may go ahead.
		const guard = useAnnotationSessionStore.getState().objectSwitchGuard;
		if (guard && !(await guard(node))) return;

		dispatch({ type: 'setAnnotationActiveNodeUuid', payload: uuid });
	};

	const renderNode = node => {
		const isActive = node.uuid === activeUuid;
		// Annotations the backend holds for this object in this project (from the summary).
		const count = node.annotation_projects?.find(
			entry => entry.project_uuid === project?.uuid
		)?.total_number_of_annotations;
		return (
			<AnnotationObjectCard
				object={node}
				mode={projectMode}
				selected={isActive}
				count={count > 0 ? count : undefined}>
				{/* The active object carries its labels inside its card, under the row line. */}
				{isActive && isAnnotating && <ObjectLabels object={node} />}
			</AnnotationObjectCard>
		);
	};

	return (
		<Flex vertical gap="small">
			<Flex vertical>
				<BaseText size="xs" type="secondary" strong style={eyebrowStyle}>
					Annotating in
				</BaseText>
				<BaseText strong ellipsis>
					{project?.name}
				</BaseText>
				{isPicking && (
					<BaseText size="sm" type="secondary">
						Draw the boundary on the map. The first object with a model is picked;
						choose another here to annotate it instead. The tile size follows the
						object.
					</BaseText>
				)}
			</Flex>

			<ObjectTree
				tree={tree}
				isLoading={isLoading}
				isError={isError}
				selectedUuid={activeUuid}
				onSelect={handleSelect}
				renderNode={renderNode}
				searchPlaceholder="Find an object"
				messages={MESSAGES}
			/>
		</Flex>
	);
};

export default AnnotationObjectTree;
